import { useState } from "react";
import { useMessages } from "../hooks/useData";

const stateConfig = {
  escalate: { label: "Escalade", color: "bg-red-100 text-red-800", reply: "⚠ Intervention requise", replyColor: "text-red-600", escalade: true },
  payment_pending: { label: "Paiement", color: "bg-amber-100 text-amber-800", reply: "⏳ En attente de paiement", replyColor: "text-amber-600" },
  payment_sent: { label: "À confirmer", color: "bg-amber-100 text-amber-800", reply: "💳 Preuve envoyée", replyColor: "text-amber-600" },
  confirmed: { label: "Confirmé", color: "bg-green-100 text-green-800", reply: "✓ Commande confirmée", replyColor: "text-green-600" },
  discovery: { label: "Nouveau", color: "bg-gray-100 text-gray-800", reply: "✓ Yasmine a répondu", replyColor: "text-green-600" },
  interested: { label: "Intéressé", color: "bg-orange-100 text-orange-800", reply: "✓ Yasmine a répondu", replyColor: "text-green-600" },
  negotiating: { label: "Négociation", color: "bg-blue-100 text-blue-800", reply: "✓ Yasmine a répondu", replyColor: "text-green-600" },
  complaint: { label: "Réclamation", color: "bg-red-100 text-red-800", reply: "⚠ Client mécontent", replyColor: "text-red-600" },
};

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Math.floor((new Date() - new Date(dateStr)) / 60000);
  if (diff < 1) return "À l'instant";
  if (diff < 60) return `${diff}min`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h`;
  return `${Math.floor(diff / 1440)}j`;
}

function ConvItem({ conv, channelColor, channelLabel }) {
  const state = conv.conversation_states?.[0]?.current_state || 'discovery';
  const stateInfo = stateConfig[state] || stateConfig.discovery;
  const lastMsg = conv.messages?.[conv.messages.length - 1];
  const initials = (conv.contacts?.phone || '??').slice(-2).toUpperCase();

  return (
    <div className="px-4 py-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer last:border-0">
      <div className="flex items-center gap-3">
        <div className="relative flex-shrink-0">
          <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center text-xs font-medium text-orange-800">
            {initials}
          </div>
          <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 ${channelColor} rounded-full border-2 border-white flex items-center justify-center text-white`} style={{fontSize:'6px'}}>
            {channelLabel}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-800">{conv.contacts?.phone || 'Inconnu'}</span>
            <span className="text-xs text-gray-400">{timeAgo(lastMsg?.created_at)}</span>
          </div>
          <div className="text-xs text-gray-500 truncate mt-0.5">{lastMsg?.content || '...'}</div>
          <div className={`text-xs mt-1 ${stateInfo.replyColor}`}>{stateInfo.reply}</div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${stateInfo.color}`}>
            {stateInfo.label}
          </span>
          <div className="flex gap-1">
            <button className="px-2 py-0.5 text-xs border border-orange-200 text-orange-600 rounded-lg hover:bg-orange-50">
              Voir
            </button>
            {stateInfo.escalade ? (
              <button className="px-2 py-0.5 text-xs border border-red-200 text-red-600 rounded-lg hover:bg-red-50">
                Répondre
              </button>
            ) : (
              <button className="px-2 py-0.5 text-xs border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50">
                Prendre la main
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="px-4 py-8 text-center text-sm text-gray-400">{message}</div>
  );
}

export default function Messages() {
  const [activeTab, setActiveTab] = useState("whatsapp");
  const { whatsappConvs, instagramConvs, facebookConvs, loading } = useMessages();

  const tabs = [
    { id: "whatsapp", label: "WhatsApp", color: "bg-green-500", label2: "WA", convs: whatsappConvs },
    { id: "instagram", label: "Instagram", color: "bg-pink-500", label2: "IG", convs: instagramConvs },
    { id: "facebook", label: "Facebook", color: "bg-blue-600", label2: "FB", convs: facebookConvs },
  ];

  const activeTabData = tabs.find(t => t.id === activeTab);

  return (
    <div>
      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors
              ${activeTab === tab.id
                ? "bg-orange-100 text-orange-800 border-orange-300"
                : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
              }`}
          >
            {tab.label}
            {tab.convs.length > 0 && (
              <span className="ml-2 bg-orange-200 text-orange-800 text-xs px-1.5 py-0.5 rounded-full">
                {tab.convs.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-400 text-sm">Chargement...</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {activeTabData?.convs.length === 0 ? (
            <EmptyState message={`Aucune conversation ${activeTabData?.label}`} />
          ) : (
            activeTabData?.convs.map((conv, i) => (
              <ConvItem
                key={i}
                conv={conv}
                channelColor={activeTabData.color}
                channelLabel={activeTabData.label2}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}